import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import walletService from '../services/walletService';
import api from '../services/api';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';
import './BuyCredit.css';

const BuyCredit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [walletBalance, setWalletBalance] = useState(0);
  const [creditAmount, setCreditAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingBalance, setFetchingBalance] = useState(true);

  // Predefined credit amounts for quick selection
  const quickAmounts = [10, 25, 50, 100, 250, 500];

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const response = await walletService.getBalance();
      if (response.success && response.data) {
        setWalletBalance(parseFloat(response.data.balance) || 0);
      } else {
        setWalletBalance(0);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      setWalletBalance(0);
    } finally {
      setFetchingBalance(false);
    }
  };

  const handlePurchaseCredit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(creditAmount);
    
    // Validation
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount < 1) {
      toast.error('Minimum credit purchase is $1.00');
      return;
    }

    if (amount > 10000) {
      toast.error('Maximum credit purchase is $10,000.00');
      return;
    }

    setLoading(true);

    try {
      // Call the mock credit purchase API via wallet service
      const response = await walletService.purchaseCredit(amount);

      if (response.success) {
        toast.success(`Successfully added $${amount.toFixed(2)} to your account!`);
        
        // Update the wallet balance
        setWalletBalance(response.data.newBalance);
        setCreditAmount('');
        
        // Redirect back to previous page or live betting after 2 seconds
        setTimeout(() => {
          const returnTo = new URLSearchParams(window.location.search).get('returnTo');
          navigate(returnTo || '/live-betting');
        }, 2000);
      } else {
        toast.error(response.message || 'Failed to purchase credit');
      }
    } catch (error) {
      console.error('Error purchasing credit:', error);
      const message = error.response?.data?.message || 'Failed to purchase credit. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAmount = (amount) => {
    setCreditAmount(amount.toString());
  };

  if (fetchingBalance) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="proskins-layout">
          <div className="proskins-content proskins-flex proskins-items-center proskins-justify-center">
            <div className="proskins-text-center">
              <div className="proskins-loading" style={{ width: '48px', height: '48px', margin: '0 auto' }}></div>
              <p className="proskins-mt-md proskins-text-secondary">Loading wallet information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="proskins-layout">
        <div className="proskins-content">
          <div className="proskins-card">
            {/* Header */}
            <div className="proskins-mb-xl">
              <button
                onClick={() => navigate(-1)}
                className="proskins-mb-md proskins-text-accent hover:opacity-80 proskins-flex proskins-items-center proskins-gap-sm"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-base)' }}
              >
                ← Back
              </button>
              <h1 className="proskins-card-title">Buy Credits</h1>
              <p className="proskins-mt-sm proskins-text-secondary">Add funds to your wallet to participate in tournaments</p>
            </div>

            {/* Current Balance */}
            <div className="proskins-card proskins-mb-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <div className="proskins-text-center">
                <h2 className="proskins-text-primary" style={{ fontSize: 'var(--font-size-lg)', fontWeight: '600' }}>Current Wallet Balance</h2>
                <p className="proskins-mt-sm proskins-text-accent" style={{ fontSize: 'var(--font-size-4xl)', fontWeight: '700' }}>${walletBalance.toFixed(2)}</p>
              </div>
            </div>

            {/* Credit Purchase Form */}
            <div className="proskins-card">
              <div className="proskins-p-xl">
                <h3 className="proskins-text-primary proskins-mb-lg" style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>Purchase Credits</h3>
                
                {/* Mock Payment Notice */}
                <div className="proskins-alert proskins-alert-warning proskins-mb-lg">
                  <div className="proskins-flex proskins-items-center proskins-gap-sm">
                    <div className="proskins-flex-shrink-0">
                      <svg className="proskins-text-warning" style={{ width: '20px', height: '20px' }} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="proskins-text-warning" style={{ fontSize: 'var(--font-size-sm)', fontWeight: '500' }}>Demo Mode</h4>
                      <p className="proskins-text-warning proskins-mt-sm" style={{ fontSize: 'var(--font-size-sm)' }}>
                        This is a demo credit purchase. No actual payment will be processed. 
                        Credits will be instantly added to your account for testing purposes.
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handlePurchaseCredit}>
                  {/* Quick Amount Selection */}
                  <div className="proskins-mb-lg">
                    <label className="proskins-label proskins-mb-md">
                      Quick Select Amount
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-sm)' }}>
                      {quickAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => handleQuickAmount(amount)}
                          className={`proskins-btn proskins-btn-secondary ${
                            creditAmount === amount.toString() ? 'proskins-btn-outline' : ''
                          }`}
                          style={{
                            padding: 'var(--spacing-md)',
                            textAlign: 'center',
                            borderRadius: 'var(--radius-md)',
                            border: creditAmount === amount.toString() ? '2px solid var(--proskins-green)' : '2px solid var(--border-secondary)',
                            backgroundColor: creditAmount === amount.toString() ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                            color: creditAmount === amount.toString() ? 'var(--proskins-green)' : 'var(--text-primary)'
                          }}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount Input */}
                  <div className="proskins-mb-lg">
                    <label htmlFor="creditAmount" className="proskins-label proskins-mb-sm">
                      Or Enter Custom Amount
                    </label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 'var(--spacing-md)', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 10 }}>
                        <span className="proskins-text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>$</span>
                      </div>
                      <input
                        id="creditAmount"
                        type="number"
                        min="1"
                        max="10000"
                        step="0.01"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        placeholder="0.00"
                        className="proskins-input"
                        style={{ paddingLeft: 'var(--spacing-2xl)' }}
                        required
                      />
                    </div>
                    <p className="proskins-mt-sm proskins-text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                      Minimum: $1.00 • Maximum: $10,000.00
                    </p>
                  </div>

                  {/* Purchase Summary */}
                  {creditAmount && parseFloat(creditAmount) > 0 && (
                    <div className="proskins-card proskins-mb-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <div className="proskins-p-lg">
                        <div className="proskins-flex proskins-justify-between proskins-items-center proskins-mb-sm">
                          <span className="proskins-text-secondary">Credit Amount:</span>
                          <span className="proskins-text-primary" style={{ fontWeight: '600' }}>${parseFloat(creditAmount).toFixed(2)}</span>
                        </div>
                        <div className="proskins-flex proskins-justify-between proskins-items-center proskins-mb-sm">
                          <span className="proskins-text-secondary">Processing Fee:</span>
                          <span className="proskins-text-accent" style={{ fontWeight: '600' }}>$0.00 (Waived)</span>
                        </div>
                        <hr style={{ borderColor: 'var(--border-primary)', margin: 'var(--spacing-sm) 0' }} />
                        <div className="proskins-flex proskins-justify-between proskins-items-center">
                          <span className="proskins-text-primary" style={{ fontSize: 'var(--font-size-lg)', fontWeight: '600' }}>Total:</span>
                          <span className="proskins-text-accent" style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700' }}>
                            ${parseFloat(creditAmount).toFixed(2)}
                          </span>
                        </div>
                        <div className="proskins-mt-sm proskins-text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                          New Balance: ${(walletBalance + parseFloat(creditAmount)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="proskins-flex proskins-gap-md">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="proskins-btn proskins-btn-secondary proskins-btn-full"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !creditAmount || parseFloat(creditAmount) <= 0}
                      className="proskins-btn proskins-btn-primary proskins-btn-full"
                    >
                      {loading ? (
                        <div className="proskins-flex proskins-items-center proskins-justify-center proskins-gap-sm">
                          <div className="proskins-loading" style={{ width: '16px', height: '16px' }}></div>
                          Processing...
                        </div>
                      ) : (
                        `Purchase $${creditAmount ? parseFloat(creditAmount).toFixed(2) : '0.00'}`
                      )}
                    </button>
                  </div>
                </form>

                {/* Security Notice */}
                <div className="proskins-mt-lg proskins-text-center">
                  <p className="proskins-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>🔒 This is a secure demo transaction. Your account will be credited instantly.</p>
                  <p className="proskins-text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>In production, this would integrate with a real payment gateway.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCredit;